import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Trash2, Mail, BookOpen, Calendar, Package, ArrowRight, Search, Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';

function FollowedSeries({ follows, onUnfollow }) {
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  return (
    <section className="mb-10" data-testid="followed-series-section">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2"><Bell className="h-5 w-5 text-rose-500" /> Séries suivies</h2>
          <p className="text-sm text-slate-500">Vous recevez un email à chaque nouveau tome</p>
        </div>
        <Link to="/series"><Button variant="ghost" size="sm" className="rounded-full text-rose-600 hover:text-rose-700" data-testid="add-series-link">Ajouter <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button></Link>
      </div>
      {follows.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-center" data-testid="no-follows">
          <p className="text-slate-500">Vous ne suivez aucune série pour l'instant</p>
          <Link to="/series"><Button size="sm" className="mt-3 rounded-full bg-rose-600 hover:bg-rose-700" data-testid="browse-series-btn"><BookOpen className="h-4 w-4 mr-2" /> Choisir mes séries</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {follows.map((f, i) => (
            <motion.div key={f.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start justify-between gap-3" data-testid={`follow-item-${f.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <Badge className={`text-xs border ${f.series.series_type === 'bd' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-rose-100 text-rose-700 border-rose-200'}`}>{f.series.series_type === 'bd' ? 'BD' : 'Manga'}</Badge>
                  {f.series.status === 'proposee' && <Badge className="text-xs border bg-slate-100 text-slate-600 border-slate-200">En cours d'ajout</Badge>}
                </div>
                <p className="font-semibold text-slate-900 truncate">{f.series.name}</p>
                {f.series.author && <p className="text-xs text-slate-500">{f.series.author}</p>}
                {f.releases?.[0] ? (
                  <p className="text-sm text-slate-500 mt-2 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {f.releases[0].volume_label} · {formatDate(f.releases[0].release_date)}</p>
                ) : (
                  <p className="text-xs text-slate-400 mt-2">Aucune sortie annoncée pour l'instant</p>
                )}
                {f.releases?.[0]?.extension_id && (
                  <Link to={`/extension/${f.releases[0].extension_id}`}><Button size="sm" className="mt-2 rounded-full text-xs" data-testid={`preorder-release-${f.id}`}>Précommander <ArrowRight className="h-3 w-3 ml-1" /></Button></Link>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-rose-600 hover:bg-rose-50 shrink-0" onClick={() => onUnfollow(f.id)} title="Ne plus suivre" data-testid={`unfollow-${f.id}`}>
                <BellOff className="h-4 w-4" />
              </Button>
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Wishlist() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || localStorage.getItem('wishlist_email') || '');
  const [items, setItems] = useState([]);
  const [follows, setFollows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadWishlist = async (e) => {
    if (e) e.preventDefault();
    if (!email) { toast.error("Veuillez entrer votre email"); return; }
    setLoading(true);
    try {
      localStorage.setItem('wishlist_email', email);
      const [wl, fl] = await Promise.all([
        api.get(`/wishlist?email=${encodeURIComponent(email)}`),
        api.get(`/series/follows?email=${encodeURIComponent(email)}`),
      ]);
      setItems(wl.data);
      setFollows(fl.data);
      setLoaded(true);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('email')) loadWishlist();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unfollow = async (followId) => {
    try {
      await api.delete(`/series/follows/${followId}?email=${encodeURIComponent(email)}`);
      setFollows(prev => prev.filter(f => f.id !== followId));
      toast.success("Vous ne suivez plus cette série");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/wishlist/${itemId}?email=${encodeURIComponent(email)}`);
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success("Retiré de votre wishlist");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
        <div className="text-center mb-10">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
            <div className="h-16 w-16 rounded-2xl bg-rose-600 flex items-center justify-center mx-auto shadow-lg shadow-rose-600/20">
              <Heart className="h-8 w-8 text-white" />
            </div>
          </motion.div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mt-6">Mes suivis</h1>
          <p className="text-base md:text-lg text-slate-500 mt-2">Vos séries suivies et les prochaines sorties BD & Manga qui vous intéressent</p>
        </div>

        <form onSubmit={loadWishlist} className="bg-white border border-slate-200 rounded-2xl p-6 mb-8" data-testid="wishlist-form">
          <Label htmlFor="wishlist-email" className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-400" /> Votre adresse email
          </Label>
          <div className="flex gap-3 mt-2">
            <Input
              id="wishlist-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="votre@email.com"
              className="flex-1 rounded-lg"
              data-testid="wishlist-email-input"
            />
            <Button type="submit" className="rounded-full px-6" disabled={loading} data-testid="wishlist-load-btn">
              {loading ? '...' : 'Voir mes suivis'}
            </Button>
          </div>
        </form>

        {loaded && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <FollowedSeries follows={follows} onUnfollow={unfollow} />
            <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2 mb-4"><Heart className="h-5 w-5 text-rose-500" /> Sorties en wishlist</h2>
            {items.length === 0 ? (
              <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl">
                <Heart className="h-12 w-12 mx-auto text-slate-200" />
                <p className="mt-4 text-slate-500 text-lg">Votre wishlist est vide</p>
                <p className="text-slate-400 text-sm mt-1">Parcourez les BD & Manga pour ajouter des sorties</p>
                <Link to="/catalogue?univers=bd_manga">
                  <Button className="mt-6 rounded-full" data-testid="browse-bd-btn">
                    <BookOpen className="h-4 w-4 mr-2" /> Voir les BD & Manga
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-500 font-medium">{items.length} élément{items.length > 1 ? 's' : ''} dans votre wishlist</p>
                {items.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow"
                    data-testid={`wishlist-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">BD & Manga</Badge>
                          {item.extension?.status && (
                            <Badge className={`text-xs border ${item.extension.status === 'ouvert' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                              {item.extension.status === 'ouvert' ? 'Précommande ouverte' : item.extension.status}
                            </Badge>
                          )}
                        </div>
                        <Link to={`/extension/${item.extension_id}`} className="font-semibold text-slate-900 hover:text-rose-600 transition-colors text-lg">
                          {item.extension_name}
                        </Link>
                        {item.release_date && (
                          <div className="flex items-center gap-1.5 mt-2 text-sm text-slate-500">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>Sortie prévue : {formatDate(item.release_date)}</span>
                          </div>
                        )}
                        {item.extension?.items && (
                          <div className="flex items-center gap-1.5 mt-1 text-sm text-slate-500">
                            <Package className="h-3.5 w-3.5" />
                            <span>{item.extension.items.length} produit{item.extension.items.length > 1 ? 's' : ''}</span>
                          </div>
                        )}
                        {item.extension?.status === 'ouvert' && (
                          <Link to={`/extension/${item.extension_id}`}>
                            <Button size="sm" className="mt-3 rounded-full text-xs" data-testid={`preorder-from-wl-${item.id}`}>
                              Précommander <ArrowRight className="h-3 w-3 ml-1" />
                            </Button>
                          </Link>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-rose-400 hover:text-rose-600 hover:bg-rose-50 shrink-0"
                        onClick={() => removeItem(item.id)}
                        data-testid={`remove-wl-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {!loaded && (
          <div className="text-center py-12">
            <Search className="h-12 w-12 mx-auto text-slate-200" />
            <p className="mt-4 text-slate-400">Entrez votre email pour voir vos séries suivies et votre wishlist</p>
          </div>
        )}
      </div>
    </div>
  );
}
